'use client';

import { format } from 'date-fns';
import { HTMLMotionProps } from 'framer-motion';
import * as React from 'react';
import { cn } from '@/lib/utils';
import type {
  TimelineColor,
  TimelineStatus,
  TimelineSubStep,
} from './timeline-types';

/**
 * Timeline component props interface
 * @interface TimelineProps
 * @extends {React.HTMLAttributes<HTMLOListElement>}
 */
interface TimelineProps extends React.HTMLAttributes<HTMLOListElement> {
  /** Size of the timeline icons */
  iconsize?: 'sm' | 'md' | 'lg';
}

/**
 * Timeline component for displaying a vertical list of events or items
 * @component
 */
const Timeline = React.forwardRef<HTMLOListElement, TimelineProps>(
  ({ className, iconsize, children, ...props }, ref) => {
    const items = React.Children.toArray(children);

    if (items.length === 0) {
      return <TimelineEmpty />;
    }

    return (
      <ol
        ref={ref}
        aria-label="Timeline"
        className={cn(
          'flex flex-col relative',
          'min-h-[600px] w-full max-w-2xl mx-auto py-8',
          className,
        )}
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (
            React.isValidElement(child) &&
            typeof child.type !== 'string' &&
            'displayName' in child.type &&
            child.type.displayName === 'TimelineItem'
          ) {
            return React.cloneElement(child, {
              iconsize,
              showConnector: index !== items.length - 1,
            } as React.ComponentProps<typeof TimelineItem>);
          }
          return child;
        })}
      </ol>
    );
  },
);
Timeline.displayName = 'Timeline';

/**
 * TimelineItem component props interface
 * @interface TimelineItemProps
 * @extends {Omit<HTMLMotionProps<"li">, "ref">}
 */
interface TimelineItemProps extends Omit<HTMLMotionProps<'li'>, 'ref'> {
  /** Date string for the timeline item */
  date?: Date;
  /** Title of the timeline item */
  title?: string;
  /** Description text */
  description?: string | React.ReactNode;
  /** Custom icon element */
  icon?: React.ReactNode;
  /** Color theme for the icon */
  iconColor?: TimelineColor;
  /** Current status of the item */
  status?: TimelineStatus;
  /** Whether to show the connector line */
  showConnector?: boolean;
  /** Whether the item is selected */
  selected?: boolean;
  /** Initiator of the item */
  initiator?: string;
  /** Size of the icon */
  iconsize?: 'sm' | 'md' | 'lg';
  /** Color of the item */
  color?: TimelineColor;
  /** Whether to show the highlight */
  showHighlight?: boolean;
  /** Sub steps of the item */
  subSteps?: TimelineSubStep[];
}

const TimelineItem = React.forwardRef<HTMLLIElement, TimelineItemProps>(
  (
    {
      className,
      date,
      title,
      initiator = 'system',
      description,
      icon,
      iconColor,
      status = 'completed',
      showConnector = true,
      selected = false,
      iconsize,
      // Omit unused Framer Motion props
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      initial,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      animate,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      transition,
      showHighlight = false,
      subSteps,
      ...props
    },
    ref,
  ) => {
    const commonClassName = cn('relative w-full mb-8 last:mb-0', className);
    const isClickable = typeof props.onClick === 'function';

    const content = (
      <div
        className={cn(
          'flex gap-4 items-stretch rounded-lg py-1 transition-colors',
          isClickable && 'hover:bg-muted/50 cursor-pointer',
          'border border-transparent',
          selected && 'bg-primary/10 border border-primary/20',
        )}
        {...(status === 'in-progress' ? { 'aria-current': 'step' } : {})}
      >
        {/* Timeline dot and connector */}
        <div className="flex flex-col items-center">
          <div className="relative z-10">
            <TimelineIcon
              icon={icon}
              color={iconColor}
              status={status}
              iconSize={iconsize}
              showHighlight={showHighlight}
            />
          </div>
          {showConnector && <div className="flex-1 w-0.5 bg-border mt-2" />}
        </div>

        {/* Content */}
        <TimelineContent>
          <div className="flex items-center gap-2 mb-1">
            <TimelineTime className="text-xs text-muted-foreground">
              {format(date, 'dd.MM.yyyy HH:mm:ss')}
            </TimelineTime>
            <TimelineInitiator>{initiator}</TimelineInitiator>
          </div>
          <TimelineHeader>
            <TimelineTitle>{title}</TimelineTitle>
          </TimelineHeader>
          <TimelineDescription>{description}</TimelineDescription>

          {/* Sub-steps */}
          {subSteps && subSteps.length > 0 && (
            <div className="mt-3 space-y-4">
              {subSteps.map((subStep, index) => (
                <div key={subStep.id || index} className="pl-3 relative">
                  <div className="h-0.5 w-[40px] bg-border absolute left-[-40px] top-[9px]" />
                  <div className="flex items-center gap-2 mb-1">
                    <TimelineTime className="text-xs text-muted-foreground">
                      {format(subStep.date, 'dd.MM.yyyy HH:mm:ss')}
                    </TimelineTime>
                    <TimelineInitiator variant="small">
                      {subStep.initiator || 'system'}
                    </TimelineInitiator>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {subStep.title}
                    </div>
                    {subStep.icon}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TimelineContent>
      </div>
    );

    // Filter out Framer Motion specific props
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
      style,
      onDrag,
      onDragStart,
      onDragEnd,
      onAnimationStart,
      onAnimationComplete,
      transformTemplate,
      whileHover,
      whileTap,
      whileDrag,
      whileFocus,
      whileInView,
      ...filteredProps
    } = props;
    /* eslint-enable @typescript-eslint/no-unused-vars */

    return (
      <li ref={ref} className={commonClassName} {...filteredProps}>
        {content}
      </li>
    );
  },
);
TimelineItem.displayName = 'TimelineItem';

const TimelineTime = React.forwardRef<
  HTMLTimeElement,
  React.HTMLAttributes<HTMLTimeElement>
>(({ className, children, ...props }, ref) => {
  return (
    <time
      ref={ref}
      className={cn(
        'text-sm font-medium tracking-tight text-muted-foreground',
        className,
      )}
      {...props}
    >
      {children}
    </time>
  );
});
TimelineTime.displayName = 'TimelineTime';

const TimelineInitiator = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & {
    variant?: 'default' | 'small';
  }
>(({ className, children, variant = 'default', ...props }, ref) => {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        'bg-secondary text-secondary-foreground border border-border',
        {
          'px-1.5 py-1 text-xs': variant === 'default',
          'px-1.5 py-0.5 text-[10px]': variant === 'small',
        },
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
});
TimelineInitiator.displayName = 'TimelineInitiator';

const TimelineConnector = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    status?: 'completed' | 'in-progress' | 'pending';
    color?: 'primary' | 'secondary' | 'muted' | 'accent';
  }
>(({ className, status = 'completed', color, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'w-0.5',
      {
        'bg-primary': color === 'primary' || (!color && status === 'completed'),
        'bg-muted': color === 'muted' || (!color && status === 'pending'),
        'bg-secondary': color === 'secondary',
        'bg-accent': color === 'accent',
        'bg-gradient-to-b from-primary to-muted':
          !color && status === 'in-progress',
      },
      className,
    )}
    {...props}
  />
));
TimelineConnector.displayName = 'TimelineConnector';

const TimelineHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-4', className)}
    {...props}
  />
));
TimelineHeader.displayName = 'TimelineHeader';

const TimelineTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'font-semibold leading-none tracking-tight text-secondary-foreground',
      className,
    )}
    {...props}
  >
    {children}
  </h3>
));
TimelineTitle.displayName = 'TimelineTitle';

const TimelineIcon = ({
  icon,
  color = 'primary',
  iconSize = 'sm',
  showHighlight = false,
}: {
  icon?: React.ReactNode;
  color?:
    | 'primary'
    | 'secondary'
    | 'muted'
    | 'accent'
    | 'destructive'
    | 'tertiary';
  status?: 'completed' | 'in-progress' | 'pending' | 'error';
  iconSize?: 'sm' | 'md' | 'lg';
  showHighlight?: boolean;
}) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const iconSizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const colorClasses = {
    primary: 'bg-primary text-primary-foreground',
    secondary: 'bg-secondary text-secondary-foreground',
    muted: 'bg-muted text-muted-foreground',
    accent: 'bg-accent text-accent-foreground',
    destructive: 'bg-destructive text-primary-foreground',
    tertiary: 'bg-violet-500 text-primary-foreground',
  };

  return (
    <div
      className={cn(
        'relative flex items-center justify-center rounded-full ring-8 ring-background shadow-sm',
        sizeClasses[iconSize],
        colorClasses[color],
      )}
    >
      {icon ? (
        <div
          className={cn(
            'flex items-center justify-center',
            iconSizeClasses[iconSize],
          )}
        >
          {icon}
        </div>
      ) : (
        <div className={cn('rounded-full', iconSizeClasses[iconSize])} />
      )}
      {showHighlight && (
        <div className="absolute -top-1 -left-1 h-3 w-3 bg-red-500 rounded-full border-2 border-background shadow-sm" />
      )}
    </div>
  );
};

const TimelineDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('max-w-sm text-sm text-muted-foreground', className)}
    {...props}
  />
));
TimelineDescription.displayName = 'TimelineDescription';

const TimelineContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2 pl-2', className)}
    {...props}
  />
));
TimelineContent.displayName = 'TimelineContent';

const TimelineEmpty = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col items-center justify-center p-8 text-center',
      className,
    )}
    {...props}
  >
    <p className="text-sm text-muted-foreground">
      {children || 'No timeline items to display'}
    </p>
  </div>
));
TimelineEmpty.displayName = 'TimelineEmpty';

export {
  Timeline,
  TimelineItem,
  TimelineConnector,
  TimelineHeader,
  TimelineTitle,
  TimelineIcon,
  TimelineDescription,
  TimelineContent,
  TimelineTime,
  TimelineInitiator,
  TimelineEmpty,
};
